import { IComment, ICreateComment } from '@sdk/contracts';
import { Form, Select } from 'antd';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ListWrapper, SelectorWrapper, Wrapper } from './ActivityCommentList.styles';
import { CommentItem } from './CommentItem';
import { CreateCommentForm } from './CreateCommentForm';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { useEventDetail, useMutateEvents } from '@/store';
import { useEventCommentList } from '@/store/events/useEventCommentList';

type Props = {
  comments: IComment[];
  eventId?: string;
  onlyResponseMode?: boolean;
};

export const ActivityCommentList = forwardRef<HTMLDivElement, Props>(
  ({ comments, eventId, onlyResponseMode = false }, ref) => {
    const { sendComment } = useMutateEvents();
    const { t } = useTranslation('CommonEventTranslations');
    const { event } = useEventDetail(eventId);
    const currentEventId = event?._id;
    const [highlightedComment, setHighlightedComment] = useState<string | null>(null);
    const { isLoading, sort, setSort } = useEventCommentList(eventId);

    const [form] = Form.useForm();
    const [isActive, setIsActive] = useState(false);
    const handleCloseForm = () => {
      setIsActive(false);
      form.resetFields();
    };
    const onCommentCreated = (comment: IComment) => {
      handleCloseForm();

      setHighlightedComment(comment._id);
      setTimeout(() => {
        const commentElement = document.getElementById(`comment-${comment._id}`);
        commentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => {
        setHighlightedComment(null);
      }, 3000);
    };

    const onFormSubmit = (values: ICreateComment) => {
      if (!currentEventId || !values.content) return;
      sendComment(
        { event: currentEventId, comment: values.content, attached: values.file },
        {
          onSuccess: (data: IComment) => onCommentCreated(data)
        }
      );
    };

    const sortOptions = [
      { label: t('MOST_USEFUL'), value: 'usefulComment' },
      { label: t('MOST_RECENT'), value: 'createdAt' }
    ];

    return (
      <Wrapper ref={ref}>
        {!onlyResponseMode && (
          <CreateCommentForm
            form={form}
            isActive={isActive}
            setIsActive={setIsActive}
            onFormSubmit={onFormSubmit}
            isLoading={isLoading}
          />
        )}
        <ListWrapper>
          <SelectorWrapper>
            <Select
              value={sort.field}
              options={sortOptions}
              size="small"
              variant="borderless"
              onChange={value => setSort({ field: value, order: 'descend' })}
            />
          </SelectorWrapper>
          {comments.map(comment => (
            <CommentItem
              key={comment._id}
              id={`comment-${comment._id}`}
              comment={comment as IComment}
              isHighlighted={highlightedComment === comment._id}
              eventId={currentEventId}
            />
          ))}
        </ListWrapper>
        {isLoading && <LoadingCircleSpinner />}
      </Wrapper>
    );
  }
);
